
import { Component, Prop, Vue } from "vue-property-decorator";
import ClickOutside from "vue-click-outside";

import NotificationDropdown from "@/components/notification/NotificationDropdown.vue";
import OpenButton from '@/components/sidebar/OpenButton.vue';
import NavbarAvatar from '@/components/navbar/NavbarAvatar.vue';
import ProfileIcon from "@/components/icons/ProfileIcon.vue";
import SettingsIcon from "@/components/icons/SettingsIcon.vue";
import SignOutIcon from "@/components/icons/SignOutIcon.vue";
// PRICING COMPONENT COMMENTED: Pricing icon import commented out
// import PricingIcon from "@/components/icons/PricingIcon.vue";
import CompanyIcon from "@/components/icons/CompanyIcon.vue";

// Components
import { User, Company } from "@/generated/graphql";
import RouteNames from "@/entities/RouteNames";
import Roles from "@/entities/Roles";

interface MenuOptions {
  title: string;
  name: string;
  icon?: string;
}

@Component({
  components: {
    NotificationDropdown,
    OpenButton,
    NavbarAvatar,
    SignOutIcon,
    SettingsIcon,
    // PRICING COMPONENT COMMENTED: Pricing icon component registration commented out
    // PricingIcon,
    ProfileIcon,
    CompanyIcon,
  },
  directives: {
    ClickOutside,
  },
})
export default class Navbar extends Vue {
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: Object, default: undefined }) readonly company?: Company;
  @Prop({ type: Boolean, default: false }) readonly auth!: boolean;
  @Prop({ type: Boolean, default: false }) readonly sidebarOpen!: boolean;

  avatarUrl: string | null = null;
  openUserMenu: boolean = false;
  openCompanyMenu: boolean = false;

  hideMenu(): void {
    this.openCompanyMenu = this.openUserMenu = false;
  }

  toggleUserMenu(): void {
    this.openUserMenu = !this.openUserMenu;
    this.openCompanyMenu = false;
  }

  toggleCompanyMenu(): void {
    this.openCompanyMenu = !this.openCompanyMenu;
    this.openUserMenu = false;
  }

  get open(): boolean {
    return this.openUserMenu || this.openCompanyMenu;
  }

  get menu(): MenuOptions[] {
    if (this.openUserMenu) {
      return this.userMenu;
    }

    if (this.openCompanyMenu) {
      return this.companyMenu;
    }

    return [];
  }

  get userAvatar(): string | null {
    return this.currentUser?.avatar ?? null;
  }

  get companyAvatar(): string | null {
    return this.company?.company_logo ?? null;
  }

  get displayName(): string {
    const givenName = this.currentUser?.given_name ?? "";
    const familyName = this.currentUser?.family_name ?? "";
    return `${givenName} ${familyName}`;
  }

  get userFullname(): string {
    const givenName = this.currentUser?.given_name ?? "";
    const familyName = this.currentUser?.family_name ?? "";
    return `${givenName} ${familyName}`;
  }

  get companyTitle(): string {
    return this.company?.business_name ?? "";
  }

  get businessName(): string | null {
    return this.company?.business_name ?? null;
  }

  get isManager(): boolean {
    return this.currentUser.role === Roles.MANAGER;
  }

  get isAdmin(): boolean {
    return this.currentUser.role === Roles.ADMIN;
  }

  get companyMenu(): MenuOptions[] {
    return [
      {
        title: "Company Profile",
        name: RouteNames.COMPANY_PROFILE,
        icon: "CompanyIcon",
      },
      {
        title: "Company Settings",
        name: RouteNames.COMPANY_INTERFACE_SETTINGS,
        icon: "SettingsIcon",
      },
    ];
  }

  get userMenu(): MenuOptions[] {
    const generalMenu = [
      {
        title: "User Profile",
        name: RouteNames.PROFILE,
        icon: "ProfileIcon",
      },
      {
        title: "Account Settings",
        name: RouteNames.USER_PROFILE,
        icon: "SettingsIcon",
      },
      {
        title: "Sign Out",
        name: RouteNames.SIGNOUT,
        icon: "SignOutIcon",
      },
    ];

    /* PRICING MENU COMMENTED: Original admin menu with pricing
    if (!this.isAdmin) {
      return generalMenu;
    }

    const adminsMenu = [...generalMenu];

    adminsMenu.splice(2, 0, {
      title: "Pricing Management",
      name: RouteNames.PRICING,
      icon: "PricingIcon",
    });

    return adminsMenu;
    */

    // Return the same menu for all users without pricing option
    return generalMenu;
  }

  iconSize(iconName: string): string {
    /* PRICING ICON COMMENTED: Original icon size check for pricing icon
    if (iconName === "PricingIcon") {
      return "h-6 w-6 -ml-0.5";
    }
    */
    return "h-5 w-5";
  }
}
